// ============================================================================
// ENUMS Y TIPOS
// ============================================================================

/** Estados de la recepción */
export type ReceptionStatus = 'UNIDENTIFIED' | 'IDENTIFYING' | 'CLOSED';

/** Estados del paquete */
export type PackageStatus = 'RECEIVED' | 'IDENTIFIED';

/** Tipos de paquete */
export type PackageType = 'BOX' | 'PALLET' | 'CONTAINER' | 'BAG' | 'SACK' | 'OTHER';

/** Tipos de destino para identificación */
export type DestinationType = 'INBOUND' | 'REJECTED' | 'RETURN' | 'SUPPLY';

/** Tipos de incidencia */
export type IncidentType = 'DAMAGED' | 'INCOMPLETE' | 'WRONG_ITEM' | string;

// ============================================================================
// MODELOS PRINCIPALES
// ============================================================================

/** Paquete dentro de una recepción */
export interface Package {
  id: number;
  reception_id: number;
  package_type: PackageType;
  package_type_other: string | null;
  quantity: number;
  status: PackageStatus;
  destination_type: DestinationType | null;
  destination_id: number | null;
  payload: Record<string, any> | null;
  incident_type: IncidentType | null;
  image_ref: string | null;
  created_at: string;
  /** Si la API incluye texto legible del destino */
  destination_label?: string | null;
  /** Referencia del destino (entrada, reused, etc.) */
  destination_reference?: string | null;
  /** Relación entrada (INBOUND) */
  entry?: { id?: number; reference?: string } | null;
  /** Relación recepción de rehusados (REJECTED / RETURN) */
  returned_reception?: { id?: number; code?: string } | null;
  reused_reception?: { id?: number; code?: string } | null;
}

/** Recepción completa con paquetes */
export interface Reception {
  id: number;
  team_id: number;
  code: string;
  carrier: string;
  vehicle_plate: string;
  driver_name: string;
  signature_ref: string;
  status: ReceptionStatus;
  closed_at: string | null;
  created_at: string;
  packages?: Package[];
  total_qty: number;
  pending_qty: number;
  identified_qty: number;
}

/** Recepción en lista (sin paquetes detallados) */
export interface ReceptionListItem {
  id: number;
  team_id: number;
  code: string;
  carrier: string;
  vehicle_plate: string;
  driver_name: string;
  signature_ref: string;
  status: ReceptionStatus;
  closed_at: string | null;
  created_at: string;
  total_qty: number;
  pending_qty: number;
  identified_qty: number;
}

// ============================================================================
// REQUEST DTOs
// ============================================================================

/** Paquete para crear/actualizar */
export interface PackageInput {
  package_type: PackageType;
  package_type_other?: string | null;
  quantity: number;
  incident_type?: IncidentType | null;
  image_ref?: string | null;
}

/** Paquete existente para actualizar */
export interface PackageUpdateInput extends PackageInput {
  id: number;
}

/** Request para crear recepción (JSON o multipart con firma en JPG) */
export interface CreateReceptionRequest {
  carrier: string;
  vehicle_plate: string;
  driver_name: string;
  /** Referencia base64 (legacy); omitir si se envía signature_file */
  signature_ref?: string;
  /** Firma como archivo JPG para multipart/form-data (recomendado) */
  signature_file?: File;
  packages: PackageInput[];
}

/** Request para actualizar recepción */
export interface UpdateReceptionRequest {
  carrier: string;
  vehicle_plate: string;
  driver_name: string;
  signature_ref: string;
  packages_to_add?: PackageInput[];
  packages_to_update?: PackageUpdateInput[];
  package_ids_to_delete?: number[];
}

/** Payload para identificar paquete - INBOUND */
export interface IdentifyPayloadInbound {
  entry_id: number;
}

/** Payload para identificar paquete - REJECTED */
export interface IdentifyPayloadRejected {
  total_orders: number;
}

/** Payload para identificar paquete - RETURN */
export interface IdentifyPayloadReturn {
  total_orders: number;
}

/** Payload para identificar paquete - SUPPLY */
export interface IdentifyPayloadSupply {
  consumable: string;
}

/** Tipo unión de todos los payloads de identificación */
export type IdentifyPayload =
  | IdentifyPayloadInbound
  | IdentifyPayloadRejected
  | IdentifyPayloadReturn
  | IdentifyPayloadSupply;

/** Request para identificar paquete */
export interface IdentifyPackageRequest {
  destination_type: DestinationType;
  destination_ref?: number | null;
  payload: IdentifyPayload;
}

/** Request para split de paquete */
export interface SplitPackageRequest {
  split_qty: number;
}

// ============================================================================
// RESPONSE Y PAGINACIÓN
// ============================================================================

/** Respuesta de lista paginada */
export interface ReceptionsApiResponse {
  data: ReceptionListItem[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
  links: {
    first: string;
    prev: string | null;
    next: string | null;
    last: string;
  };
}

/** Página para infinite scroll */
export interface ReceptionPage {
  receptions: ReceptionListItem[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

// ============================================================================
// FILTROS
// ============================================================================

/** Filtros para búsqueda de recepciones */
export interface ReceptionFilters {
  status?: ReceptionStatus;
  date_from?: string;
  date_to?: string;
  carrier?: string;
  code?: string;
  per_page?: number;
  page?: number;
  sort_column?: 'created_at' | 'code' | 'carrier' | 'status';
  sort_direction?: 'asc' | 'desc';
}

// ============================================================================
// ERRORES
// ============================================================================

/** Códigos de error de la API */
export type ReceptionErrorCode =
  | 'RECEPTION_NOT_FOUND'
  | 'PACKAGE_NOT_FOUND'
  | 'RECEPTION_CLOSED'
  | 'PACKAGE_ALREADY_IDENTIFIED'
  | 'INVALID_PACKAGE_TYPE_OTHER'
  | 'INVALID_SPLIT_QUANTITY'
  | 'INVALID_PAYLOAD'
  | 'CANNOT_DELETE_IDENTIFIED_RECEPTION';

/** Error de la API */
export interface ReceptionApiError {
  code: ReceptionErrorCode;
  message: string;
}

// ============================================================================
// HELPERS Y CONSTANTES
// ============================================================================

/** Opciones de tipo de paquete para formularios */
export const PACKAGE_TYPE_OPTIONS: { value: PackageType; labelKey: string }[] = [
  { value: 'BOX', labelKey: 'package_types.box' },
  { value: 'PALLET', labelKey: 'package_types.pallet' },
  { value: 'CONTAINER', labelKey: 'package_types.container' },
  { value: 'BAG', labelKey: 'package_types.bag' },
  { value: 'SACK', labelKey: 'package_types.sack' },
  { value: 'OTHER', labelKey: 'package_types.other' },
];

/** Opciones de tipo de destino para formularios */
export const DESTINATION_TYPE_OPTIONS: { value: DestinationType; labelKey: string }[] = [
  { value: 'INBOUND', labelKey: 'destination_types.inbound' },
  { value: 'REJECTED', labelKey: 'destination_types.rejected' },
];

/** Opciones de estado de recepción para filtros */
export const RECEPTION_STATUS_OPTIONS: { value: ReceptionStatus; labelKey: string }[] = [
  { value: 'UNIDENTIFIED', labelKey: 'status.unidentified' },
  { value: 'IDENTIFYING', labelKey: 'status.identifying' },
  { value: 'CLOSED', labelKey: 'status.closed' },
];

/** Verifica si una recepción puede ser editada */
export const canEditReception = (status: ReceptionStatus): boolean => {
  return ['UNIDENTIFIED', 'IDENTIFYING', 'CLOSED'].includes(status);
};

/** Verifica si una recepción puede ser eliminada */
export const canDeleteReception = (reception: Reception | ReceptionListItem): boolean => {
  return reception.status === 'UNIDENTIFIED' && reception.identified_qty === 0;
};

/** Verifica si un paquete puede ser identificado */
export const canIdentifyPackage = (pkg: Package, receptionStatus: ReceptionStatus): boolean => {
  return pkg.status === 'RECEIVED' && receptionStatus !== 'CLOSED';
};

/** Verifica si un paquete puede ser dividido (split) */
export const canSplitPackage = (pkg: Package): boolean => {
  return pkg.status !== 'IDENTIFIED' && pkg.quantity > 1;
};

/** Obtiene los campos requeridos del payload según el tipo de destino */
export const getRequiredPayloadFields = (destinationType: DestinationType): string[] => {
  switch (destinationType) {
    case 'INBOUND':
      return ['entry_id'];
    case 'REJECTED':
    case 'RETURN':
      return ['total_orders'];
    case 'SUPPLY':
      return ['consumable'];
    default:
      return [];
  }
};

/** Construye la URL del destino según el tipo de destino y su ID */
export const getDestinationUrl = (
  destinationType: DestinationType | null,
  destinationId: number | null,
): string | null => {
  if (!destinationType || !destinationId) return null;

  switch (destinationType) {
    case 'REJECTED':
      return `/reused/${destinationId}/receive`;
    case 'INBOUND':
      return `/merchandise/edit/${destinationId}`;
    default:
      return null;
  }
};

/**
 * Referencia legible del destino (entrada de mercancía, recepción reused, etc.).
 * Usa campos enriquecidos de la API, relaciones o payload cuando existan.
 */
export const getPackageDestinationReference = (pkg: Package): string | null => {
  const trimStr = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

  if (trimStr(pkg.destination_label)) return pkg.destination_label!.trim();
  if (trimStr(pkg.destination_reference)) return pkg.destination_reference!.trim();
  if (pkg.entry?.reference && trimStr(pkg.entry.reference)) return pkg.entry.reference.trim();
  if (pkg.returned_reception?.code && trimStr(pkg.returned_reception.code)) return pkg.returned_reception.code.trim();
  if (pkg.reused_reception?.code && trimStr(pkg.reused_reception.code)) return pkg.reused_reception.code.trim();

  const extra = pkg as Package & Record<string, unknown>;
  const er = trimStr(extra.entry_reference);
  if (er) return er;
  const rc = trimStr(extra.reused_code);
  if (rc) return rc;
  if (extra.destination && typeof extra.destination === 'object' && extra.destination !== null) {
    const d = extra.destination as Record<string, unknown>;
    const code = trimStr(d.code) ?? trimStr(d.reference);
    if (code) return code;
  }

  const payload = pkg.payload;
  if (payload && typeof payload === 'object') {
    const pl = payload as Record<string, unknown>;
    const fromPayload =
      trimStr(pl.entry_reference) ?? trimStr(pl.reference) ?? trimStr(pl.reused_code) ?? trimStr(pl.code);
    if (fromPayload) return fromPayload;
  }

  return null;
};
